import React, { useState, useMemo } from 'react';
import { RowTab } from '../types';
import { useCoinRegistry } from '../../../shared/CoinRegistryContext';
import { apiFetch } from '../../../auth/AuthContext';

/** Fire-and-forget download log to auth_server. */
function logDownload(requestId: string) {
  apiFetch(`/parquet/download?request_id=${encodeURIComponent(requestId)}`, {
    method: 'POST',
  }).catch(() => {});
}

interface RowTabCardProps {
  rowTab: RowTab;
  onEdit: (rowTab: RowTab) => void;
  onDownload: (rowTab: RowTab) => void;
  onDelete: (rowTab: RowTab) => void;
  downloading?: boolean;
  downloadBytes?: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function RowTabCard({
  rowTab,
  onEdit,
  onDownload,
  onDelete,
  downloading,
  downloadBytes = 0,
}: RowTabCardProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { disabledCoins } = useCoinRegistry();

  const disabledInRow = useMemo(
    () => rowTab.coins.filter((c) => disabledCoins.has(c)),
    [rowTab.coins, disabledCoins],
  );
  const hasDisabled = disabledInRow.length > 0;

  // Builds a RowTab with disabled coins stripped out
  const rowTabWithoutDisabled = useMemo(
    (): RowTab => ({
      ...rowTab,
      coins: rowTab.coins.filter((c) => !disabledCoins.has(c)),
    }),
    [rowTab, disabledCoins],
  );

  const [showDisabledPrompt, setShowDisabledPrompt] = useState<{
    label: string;
    action: (filtered: RowTab) => void;
  } | null>(null);

  const guardAction = (action: (tab: RowTab) => void, label: string) => {
    if (hasDisabled) {
      setShowDisabledPrompt({ label, action });
      return;
    }
    action(rowTab);
  };

  return (
    <div className="pq-card">
      {/* Disabled coin prompt overlay */}
      {showDisabledPrompt && (
        <div className="pq-delete-overlay">
          <div className="pq-delete-dialog">
            <p className="pq-delete-msg">
              Disabled coin{disabledInRow.length > 1 ? 's' : ''} detected
            </p>
            <p className="pq-delete-sub">
              <strong>{disabledInRow.join(', ')}</strong>{' '}
              {disabledInRow.length === 1 ? 'is' : 'are'} disabled and will not
              be pulled from ClickHouse.
            </p>
            <p
              className="pq-delete-sub"
              style={{ marginTop: 4, color: '#8b949e' }}
            >
              Proceed with only enabled coins (
              {rowTabWithoutDisabled.coins.join(', ') || 'none'})?
            </p>
            <div className="pq-delete-actions">
              <button
                className="pq-btn pq-btn-ghost"
                onClick={() => setShowDisabledPrompt(null)}
              >
                Cancel
              </button>
              {rowTabWithoutDisabled.coins.length > 0 && (
                <button
                  className="pq-btn pq-btn-card-edit"
                  onClick={() => {
                    const { action } = showDisabledPrompt;
                    setShowDisabledPrompt(null);
                    action(rowTabWithoutDisabled);
                  }}
                >
                  Proceed without {disabledInRow.join(', ')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation overlay */}
      {confirmingDelete && (
        <div className="pq-delete-overlay">
          <div className="pq-delete-dialog">
            <p className="pq-delete-msg">
              Delete <strong>"{rowTab.name}"</strong>?
            </p>
            <p className="pq-delete-sub">This action cannot be undone.</p>
            <div className="pq-delete-actions">
              <button
                className="pq-btn pq-btn-ghost"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
              <button
                className="pq-btn pq-btn-card-delete"
                onClick={() => {
                  setConfirmingDelete(false);
                  onDelete(rowTab);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pq-card-header">
        <h3 className="pq-card-title">{rowTab.name}</h3>
        <span className={`pq-card-source ${rowTab.dataSource}`}>
          {rowTab.dataSource === 'cleaned' ? 'Cleaned' : 'With Spikes'}
        </span>
      </div>

      <div className="pq-card-body">
        <div className="pq-card-row">
          <span className="pq-card-label">Timeframes</span>
          <div className="pq-card-tags">
            {rowTab.timeframes.map((tf) => (
              <span key={tf} className="pq-tag tf">
                {tf}
              </span>
            ))}
          </div>
        </div>

        <div className="pq-card-row">
          <span className="pq-card-label">Coins</span>
          <div className="pq-card-tags">
            {rowTab.coins.map((c) => (
              <span
                key={c}
                className={`pq-tag coin ${disabledCoins.has(c) ? 'pq-tag--disabled' : ''}`}
                title={disabledCoins.has(c) ? `${c} is disabled` : undefined}
              >
                {c}
              </span>
            ))}
          </div>
        </div>

        <div className="pq-card-row">
          <span className="pq-card-label">Data Types</span>
          <div className="pq-card-tags">
            {rowTab.dataTypes.map((dt) => (
              <span key={dt} className="pq-tag dt">
                {dt}
              </span>
            ))}
          </div>
        </div>

        <div className="pq-card-row">
          <span className="pq-card-label">Date Range</span>
          <span className="pq-card-value">
            {rowTab.dateFrom} &rarr; {rowTab.dateTo}
          </span>
        </div>
      </div>

      <div className="pq-card-actions">
        <button
          className="pq-btn pq-btn-card-edit"
          onClick={() => onEdit(rowTab)}
        >
          Edit
        </button>
        <button
          className="pq-btn pq-btn-card-download"
          onClick={() =>
            guardAction((tab) => {
              logDownload(rowTab.id);
              onDownload(tab);
            }, 'download')
          }
          disabled={downloading}
        >
          {downloading
            ? downloadBytes > 0
              ? `Generating... ${formatBytes(downloadBytes)}`
              : 'Generating...'
            : 'Download'}
        </button>
        <button
          className="pq-btn pq-btn-card-delete"
          onClick={() => setConfirmingDelete(true)}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

export default RowTabCard;
